import { useRowSelectionContext } from 'components/atoms/RowSelectionContext';
import { filter, flatMap, forEach, includes, isEmpty, map, some, uniq } from 'es-toolkit/compat';
import { getInnerRowIdFromUnwoundRowMetadata } from 'interfaces/genericFields/unwindRowsWithInnerArrays';
import { Procedure } from 'interfaces/procedure';
import { Slide } from 'interfaces/slide';
import { ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from 'redux/hooks';

import { createIndividualCasesForSlides } from '../ProceduresPage/usePendingSlides';
import { ManifestWorkspaceSurface } from './CaseManifestControls';
import { CaseManifestSourcePanel } from './CaseManifestSourcePanel';
import {
  buildSingleCaseManifestDraft,
  buildUnassignedCaseManifestDraft,
  ManifestCaseTarget,
  ManifestDialogUrlState,
  SlideAdditionChoice,
} from './dialogState';
import { revalidateSlideCaseAssignmentOptions } from './loadSlideCaseAssignmentOptions';
import { addManifestDraftCases } from './manifestDraft';
import { SlideSelectionTableRow } from './SlideSelectionTable';
import { useSlideCaseAssignmentOptions } from './useSlideCaseAssignmentOptions';

const setSlideAssignmentTargets = (
  assignmentsBySlideId: Record<string, ManifestCaseTarget>,
  slideIds: string[],
  target: ManifestCaseTarget
) => {
  const nextAssignments = { ...assignmentsBySlideId };
  forEach(slideIds, (slideId) => {
    nextAssignments[slideId] = target;
  });
  return nextAssignments;
};

const getChoiceTarget = (choice: SlideAdditionChoice): ManifestCaseTarget =>
  choice.kind === 'existingCase' ? choice.caseId : choice.kind === 'assignManually' ? null : 'new';

interface ManifestSourceSelectionOptions {
  open: boolean;
  labId: string;
  studyId: string;
  activeWorkspaceSurface: ManifestWorkspaceSurface;
  currentDraftCases: Procedure[];
  pendingSlidesCasesToDisplay: Procedure[];
  sourceScope: 'study' | 'lab';
  canReadActiveSource: boolean;
  canReadStudySource: boolean;
  canIncludeAssigned: boolean;
  isPendingSlidesLoading: boolean;
  activePendingSlidesError: unknown;
  pendingSlidesSelectionAction: ManifestDialogUrlState['pendingSlidesSelectionAction'];
  disabled: boolean;
  updateManifestDialogUrlState: (
    updates: Partial<ManifestDialogUrlState> | ((previousState: ManifestDialogUrlState) => ManifestDialogUrlState)
  ) => void;
}

export const useManifestSourceSelection = ({
  open,
  labId,
  studyId,
  activeWorkspaceSurface,
  currentDraftCases,
  pendingSlidesCasesToDisplay,
  sourceScope,
  canReadActiveSource,
  canReadStudySource,
  canIncludeAssigned,
  isPendingSlidesLoading,
  activePendingSlidesError,
  pendingSlidesSelectionAction,
  disabled,
  updateManifestDialogUrlState,
}: ManifestSourceSelectionOptions) => {
  const { profile, generationId } = useAppSelector((state) => state.auth);
  const { selectedRows } = useRowSelectionContext();
  const [selectedSourceSlideIds, setSelectedSourceSlideIds] = useState<string[]>(() =>
    getInnerRowIdFromUnwoundRowMetadata(selectedRows as string[])
  );
  const [assignedSource, setAssignedSource] = useState<{ key: string; includeAssigned: boolean } | null>(null);
  const [isRevalidatingSource, setIsRevalidatingSource] = useState(false);
  const [sourceSelectionMessage, setSourceSelectionMessage] = useState<string | null>(null);
  const sourceValidationController = useRef<AbortController | null>(null);
  const isSlideSource =
    activeWorkspaceSurface === 'Study Lost and Found Slides' || activeWorkspaceSurface === 'Lab Lost and Found Slides';
  const sourceIdentity = JSON.stringify([profile?.userId, generationId, labId, studyId, activeWorkspaceSurface]);
  const includeAssigned = assignedSource?.key === sourceIdentity && assignedSource.includeAssigned;
  const assignedOptions = useSlideCaseAssignmentOptions({
    request: { labId, targetStudyId: studyId, scope: sourceScope, includeAssigned: true },
    callerId: profile?.userId || '',
    generationId: generationId || '',
    enabled: open && isSlideSource && includeAssigned && canIncludeAssigned && canReadActiveSource,
  });
  const draftSlideIdentity = JSON.stringify(map(flatMap(currentDraftCases, 'slides'), 'id'));
  useEffect(() => {
    sourceValidationController.current?.abort();
    setIsRevalidatingSource(false);
    setSourceSelectionMessage(null);
    return () => sourceValidationController.current?.abort();
  }, [open, sourceIdentity, includeAssigned, canReadActiveSource, canIncludeAssigned, draftSlideIdentity]);
  const isSourceLoading =
    isRevalidatingSource || (includeAssigned ? assignedOptions.isLoading : isPendingSlidesLoading);
  const sourceUnavailable =
    !canReadActiveSource || (includeAssigned ? !assignedOptions.isComplete : Boolean(activePendingSlidesError));
  const pendingSlideSelectionRows = useMemo<SlideSelectionTableRow[]>(
    () =>
      includeAssigned
        ? map(
            filter(
              assignedOptions.items || [],
              ({ slide }) => !some(currentDraftCases, (draftCase) => some(draftCase.slides, { id: slide.id }))
            ),
            ({ slide, isAssigned, assignments, hasRestrictedAssignments }) => ({
              slide,
              sourceContextItems: slide.sourceStudyId ? [`Source study: ${slide.sourceStudyId}`] : [],
              assignment: isAssigned
                ? [
                    ...map(
                      assignments,
                      ({ caseId, caseLabel, studyId: owningStudyId }) =>
                        `${caseLabel} (case ${caseId}, study ${owningStudyId})`
                    ),
                    ...(hasRestrictedAssignments ? ['Assigned to a restricted case'] : []),
                  ].join('; ')
                : 'Needs case assignment',
            })
          )
        : flatMap(pendingSlidesCasesToDisplay, (pendingCase) =>
            map(pendingCase.slides, (slide) => ({
              slide: { ...slide, labId: slide.labId || pendingCase.labId },
              sourceContextItems: filter(
                [
                  slide.sourceStudyId ? `Source study: ${slide.sourceStudyId}` : undefined,
                  pendingCase.labId && pendingCase.labId !== labId ? `Source lab: ${pendingCase.labId}` : undefined,
                ],
                (context): context is string => Boolean(context)
              ),
              assignment: 'Needs case assignment',
            }))
          ),
    [labId, pendingSlidesCasesToDisplay, includeAssigned, assignedOptions.items, currentDraftCases]
  );

  const addCasesToManifest = useCallback(
    (casesToAdd: Procedure[], targetCase: ManifestCaseTarget = 'new') => {
      if (isEmpty(casesToAdd)) return;
      updateManifestDialogUrlState((previousState) => addManifestDraftCases(previousState, casesToAdd, targetCase));
    },
    [updateManifestDialogUrlState]
  );
  const addSelectedSlidesToManifest = useCallback(
    async ({ choice, scope }: { choice: SlideAdditionChoice; scope: 'study' | 'lab' }) => {
      if (!canIncludeAssigned || !selectedSourceSlideIds.length) return false;
      sourceValidationController.current?.abort();
      const controller = new AbortController();
      sourceValidationController.current = controller;
      setIsRevalidatingSource(true);
      setSourceSelectionMessage(null);
      try {
        const items = await revalidateSlideCaseAssignmentOptions(
          { labId, targetStudyId: studyId, scope },
          selectedSourceSlideIds,
          controller.signal
        );
        if (controller.signal.aborted) return;
        const validItems = filter(
          items,
          ({ slide }) =>
            includes(selectedSourceSlideIds, slide.id) &&
            !some(currentDraftCases, (draftCase) => some(draftCase.slides, { id: slide.id }))
        );
        const validIds = map(validItems, ({ slide }) => slide.id);
        const missingCount = selectedSourceSlideIds.length - validIds.length;
        if (missingCount > 0) {
          setSelectedSourceSlideIds(validIds);
          setSourceSelectionMessage(
            `${missingCount} selected slides are no longer available in this source or already in the case assignment. Review the remaining selection before adding.`
          );
          return false;
        }
        const slides = map(validItems, 'slide');
        const draftCases =
          choice.kind === 'singleCase'
            ? buildSingleCaseManifestDraft({ slides, labId })
            : choice.kind === 'assignManually'
            ? buildUnassignedCaseManifestDraft({ slides, labId })
            : createIndividualCasesForSlides(slides, labId);
        addCasesToManifest(draftCases, getChoiceTarget(choice));
        setSelectedSourceSlideIds([]);
        return true;
      } catch (error) {
        if (!controller.signal.aborted) {
          setSourceSelectionMessage(
            'Could not verify the selected slides. Your selection and case assignment are unchanged. Try adding again.'
          );
        }
        return false;
      } finally {
        if (sourceValidationController.current === controller && !controller.signal.aborted)
          setIsRevalidatingSource(false);
      }
    },
    [addCasesToManifest, canIncludeAssigned, currentDraftCases, labId, selectedSourceSlideIds, studyId]
  );
  const addSelectedPendingSlidesToManifest = useCallback(
    async (choice: SlideAdditionChoice) => {
      if (isSourceLoading || sourceUnavailable) return false;
      return addSelectedSlidesToManifest({ choice, scope: sourceScope });
    },
    [addSelectedSlidesToManifest, isSourceLoading, sourceScope, sourceUnavailable]
  );
  // Dataset-selected slides belong to this study, so the action intent reads study scope on any workspace surface.
  useEffect(() => {
    if (!pendingSlidesSelectionAction || isPendingSlidesLoading || isRevalidatingSource || !canReadStudySource) return;
    updateManifestDialogUrlState({ pendingSlidesSelectionAction: undefined });
    void addSelectedSlidesToManifest({
      choice:
        pendingSlidesSelectionAction === 'groupSelectedSlidesIntoSingleCase'
          ? { kind: 'singleCase' }
          : { kind: 'individualCases' },
      scope: 'study',
    });
  }, [
    pendingSlidesSelectionAction,
    isPendingSlidesLoading,
    isRevalidatingSource,
    canReadStudySource,
    updateManifestDialogUrlState,
    addSelectedSlidesToManifest,
  ]);
  const addDirectSlidesToManifest = useCallback(
    (slides: Slide[], choice: SlideAdditionChoice) =>
      updateManifestDialogUrlState((previousState) => ({
        ...previousState,
        manualManifestSlideIdsToLoad: uniq([
          ...(previousState.manualManifestSlideIdsToLoad || []),
          ...map(slides, 'id'),
        ]),
        manualManifestGroupIntoSingleCase: choice.kind === 'singleCase',
        existingCaseAssignmentsBySlideId: setSlideAssignmentTargets(
          previousState.existingCaseAssignmentsBySlideId || {},
          map(slides, 'id'),
          getChoiceTarget(choice)
        ),
        manualManifestErrorMessages: [],
      })),
    [updateManifestDialogUrlState]
  );

  const selectedPendingSlidesCount = selectedSourceSlideIds.length;
  const sourceName =
    activeWorkspaceSurface === 'Study Lost and Found Slides'
      ? 'slides awaiting assignment'
      : 'lab lost and found slides';
  const scopeName = activeWorkspaceSurface === 'Study Lost and Found Slides' ? 'this study' : 'this lab';
  const sourcePanelProps: ComponentProps<typeof CaseManifestSourcePanel> = {
    canIncludeAssigned,
    canReadSource: canReadActiveSource,
    description:
      activeWorkspaceSurface === 'Study Lost and Found Slides'
        ? 'Select slides uploaded to this study to add to the case assignment.'
        : 'Select slides from other studies in this lab to add to the case assignment.',
    disabled,
    hasAssignedError: Boolean(assignedOptions.error),
    hasPendingError: Boolean(activePendingSlidesError),
    includeAssigned: Boolean(includeAssigned),
    isLoading: isSourceLoading,
    loadingText: isRevalidatingSource
      ? 'Verifying selected slides...'
      : includeAssigned
      ? `Loading slides and assignments... ${assignedOptions.loadedCount} slides loaded`
      : `Loading ${sourceName}...`,
    onIncludeAssignedChange: (checked) => setAssignedSource({ key: sourceIdentity, includeAssigned: checked }),
    onRetry: () => void assignedOptions.refetch(),
    onSelectedSlideIdsChange: setSelectedSourceSlideIds,
    rows: pendingSlideSelectionRows,
    scopeName,
    selectedSlideIds: selectedSourceSlideIds,
    selectionMessage: sourceSelectionMessage,
    selectionScopeKey: `${sourceIdentity}:${includeAssigned}`,
    sourceName,
    sourceUnavailable,
  };
  return {
    addDirectSlidesToManifest,
    addSelectedPendingSlidesToManifest,
    selectedPendingSlidesCount,
    sourcePanelProps,
  };
};
